// frontend/src/pages/SeasonPage.tsx
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft, Calendar, Film, Loader } from 'lucide-react';
import MovieCard from '../components/MovieCard';
import { moviesAPI } from '../services/api';

interface Movie {
  id: number;
  title: string;
  overview?: string;
  posterUrl?: string;
  releaseYear?: number;
  voteAverage?: number;
  season?: string;
  holiday?: string;
  moods?: string[];
  tropes?: string[];
  favoritesCount?: number;
  reviewsCount?: number;
}

const seasonData: Record<string, {
  title: string;
  description: string;
  icon: string;
  color: string;
  themes: string[];
  perfectFor: string[];
  longDescription: string;
}> = {
  winter: {
    title: 'Winter Warmers',
    description: 'Cozy up with these winter romance classics. Perfect for snowy nights and warm blankets.',
    icon: '❄️',
    color: 'bg-season-winter',
    longDescription: "Winter romcoms are all about cozy moments, holiday magic, and finding warmth in the coldest season. From Christmas meet-cutes to New Year's resolutions, these films capture the intimate, reflective nature of winter while keeping hearts warm with laughter and love.",
    themes: ['Holiday magic', 'Cozy moments', 'Second chances', 'Family gatherings', 'Snowy meet-cutes'],
    perfectFor: ['Christmas movie marathons', 'Snowy nights in', 'Hot cocoa dates', 'Holiday gatherings'],
  },
  spring: {
    title: 'Spring Blossoms',
    description: 'Fresh starts and new beginnings. These romcoms capture the hopeful spirit of spring.',
    icon: '🌸',
    color: 'bg-season-spring',
    longDescription: 'Spring romcoms symbolize new beginnings, growth, and blossoming relationships. They often feature characters emerging from their shells, taking chances, and discovering love in the most unexpected places.',
    themes: ['New beginnings', 'Personal growth', 'Unexpected love', 'Taking chances', 'Renewal'],
    perfectFor: ['Picnic dates', 'Sunday afternoons', 'When you need inspiration', 'Fresh start moments'],
  },
  summer: {
    title: "Summer Lovin'",
    description: 'Sunny days and warm nights. Beach romances and vacation flings await.',
    icon: '☀️',
    color: 'bg-season-summer',
    longDescription: "Summer lovin' happens so fast! These romcoms take advantage of sunny weather, vacations, and the carefree spirit of summer. Whether it's a beach romance or a European adventure, these films capture the excitement of warm-weather romance.",
    themes: ['Vacation romance', 'Summer flings', 'Adventure', 'Beach settings', 'Spontaneity'],
    perfectFor: ['Beach days', 'Summer vacations', 'Poolside viewing', 'When you need adventure'],
  },
  autumn: {
    title: 'Autumn Romance',
    description: 'Fall in love all over again. Cozy sweaters, falling leaves, and heartfelt connections.',
    icon: '🍂',
    color: 'bg-season-autumn',
    longDescription: 'Autumn romcoms blend cozy atmospheres with heartfelt connections. The changing leaves and cooler weather create intimate settings for characters to open up, be vulnerable, and build deep relationships.',
    themes: ['Cozy atmospheres', 'Vulnerability', 'Deep connections', 'Comfort', 'Change'],
    perfectFor: ['Blanket forts', 'Pumpkin spice season', 'Rainy days', 'Cozy nights in'],
  },
};

const SeasonPage: React.FC = () => {
  const { season } = useParams<{ season: string }>();
  const navigate = useNavigate();
  const [movies, setMovies] = useState<Movie[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [total, setTotal] = useState(0);
  const [isLoadingMore, setIsLoadingMore] = useState(false);

  const currentSeason = season || 'winter';
  const info = seasonData[currentSeason] || seasonData.winter;

  useEffect(() => {
    const fetchSeasonMovies = async () => {
      try {
        setIsLoading(true);
        setError('');
        const response = await moviesAPI.getBySeason(currentSeason, 1);
        const { movies: newMovies, pagination } = response.data.data;
        setMovies(newMovies);
        setTotal(pagination.total);
        setHasMore(pagination.total > newMovies.length);
        setPage(1);
      } catch (err) {
        setError('Failed to load movies. Please try again.');
        console.error(err);
      } finally {
        setIsLoading(false);
      }
    };

    fetchSeasonMovies();
  }, [currentSeason]);

  const loadMore = async () => {
    try {
      setIsLoadingMore(true);
      const nextPage = page + 1;
      const response = await moviesAPI.getBySeason(currentSeason, nextPage);
      const { movies: newMovies, pagination } = response.data.data;
      setMovies(prev => [...prev, ...newMovies]);
      setPage(nextPage);
      setHasMore(movies.length + newMovies.length < pagination.total);
    } catch (err) {
      console.error(err);
    } finally {
      setIsLoadingMore(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-romcom-pink/10 to-romcom-blue/10">
      {/* Back Button */}
      <button
        onClick={() => navigate(-1)}
        className="fixed top-24 left-4 z-10 bg-[#faf8f6] backdrop-blur-sm p-3 rounded-full shadow-lg hover:scale-105 transition-transform"
      >
        <ArrowLeft className="w-6 h-6" />
      </button>

      <div className="container mx-auto px-4 pt-24 pb-16">
        {/* Season Header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className={`${info.color} rounded-3xl p-8 md:p-12 text-white mb-12`}
        >
          <div className="flex flex-col md:flex-row items-center justify-between">
            <div className="mb-6 md:mb-0">
              <div className="flex items-center mb-4">
                <span className="text-6xl mr-4">{info.icon}</span>
                <div>
                  <h1 className="text-4xl md:text-5xl font-playfair font-bold mb-2">
                    {info.title}
                  </h1>
                  <div className="flex items-center">
                    <Calendar className="w-5 h-5 mr-2" />
                    <span className="text-lg capitalize">{currentSeason} Collection</span>
                  </div>
                </div>
              </div>
              <p className="text-xl opacity-90 max-w-3xl">{info.description}</p>
            </div>
            <div className="bg-[#faf8f6] backdrop-blur-sm rounded-xl p-6 text-center min-w-[120px]">
              <div className="text-4xl font-bold mb-2">
                {isLoading ? '...' : total.toLocaleString()}
              </div>
              <div className="opacity-90">Romcoms</div>
            </div>
          </div>
        </motion.div>

        {/* Loading */}
        {isLoading && (
          <div className="flex flex-col items-center justify-center py-24">
            <Loader className="w-10 h-10 text-romcom-red animate-spin mb-4" />
            <p className="text-gray-500">Loading {currentSeason} romcoms...</p>
          </div>
        )}

        {/* Error */}
        {error && !isLoading && (
          <div className="text-center py-16">
            <div className="text-5xl mb-4">😔</div>
            <p className="text-gray-600 mb-4">{error}</p>
            <button
              onClick={() => window.location.reload()}
              className="px-6 py-3 bg-romcom-red text-white rounded-full hover:bg-red-600 transition-all"
            >
              Try Again
            </button>
          </div>
        )}

        {/* Movies Grid */}
        {!isLoading && !error && (
          <div className="mb-8">
            <div className="flex items-center justify-between mb-8">
              <h2 className="text-3xl font-playfair font-bold text-gray-800">
                <Film className="inline-block w-8 h-8 mr-3" />
                {total.toLocaleString()} {total === 1 ? 'Movie' : 'Movies'} in this Collection
              </h2>
            </div>

            {movies.length > 0 ? (
              <>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                  {movies.map((movie, index) => (
                    <motion.div
                      key={movie.id}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: Math.min(index * 0.05, 0.5) }}
                    >
                      <MovieCard movie={movie} />
                    </motion.div>
                  ))}
                </div>

                {/* Load More */}
                {hasMore && (
                  <div className="text-center mt-12">
                    <button
                      onClick={loadMore}
                      disabled={isLoadingMore}
                      className="px-8 py-3 bg-[#faf8f6] border-2 border-romcom-red text-romcom-red rounded-full font-medium hover:bg-romcom-red hover:text-white transition-all disabled:opacity-50 flex items-center gap-2 mx-auto"
                    >
                      {isLoadingMore ? (
                        <><Loader className="w-4 h-4 animate-spin" /> Loading...</>
                      ) : (
                        `Load More (${(total - movies.length).toLocaleString()} remaining)`
                      )}
                    </button>
                  </div>
                )}
              </>
            ) : (
              <div className="text-center py-16">
                <div className="text-6xl mb-4">🎬</div>
                <h3 className="text-2xl font-bold text-gray-800 mb-2">No movies found</h3>
                <p className="text-gray-600">Check back soon for more {currentSeason} romcoms!</p>
              </div>
            )}
          </div>
        )}

        {/* Season Info Card */}
        <div className="bg-[#faf8f6] rounded-3xl shadow-xl p-8 border border-romcom-pink/20">
          <h3 className="text-2xl font-bold text-gray-800 mb-4">About {info.title}</h3>
          <p className="text-gray-700 text-lg leading-relaxed mb-6">{info.longDescription}</p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <h4 className="font-bold text-gray-800 mb-2">Common Themes</h4>
              <ul className="list-disc pl-5 text-gray-700 space-y-1">
                {info.themes.map((theme, i) => <li key={i}>{theme}</li>)}
              </ul>
            </div>
            <div>
              <h4 className="font-bold text-gray-800 mb-2">Perfect For</h4>
              <ul className="list-disc pl-5 text-gray-700 space-y-1">
                {info.perfectFor.map((item, i) => <li key={i}>{item}</li>)}
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SeasonPage;